/**
 * `<TourRepresentationSwitch />` — attached-mode representation toggle
 * (RFC-120 §3).
 *
 * Shown when the scene contains a panorama bubble tour AND a point cloud
 * that share a converter-stamped `sourceGroupId` (both artifacts derive
 * from the same E57). Lets the operator pivot between walking the bubble
 * tour and inspecting the raw point cloud without hunting through the
 * scene tree.
 *
 * Deliberately mirrors `<PointCloudModeSwitch />` (A10): two pressable
 * chips with toggled `aria-pressed` instead of a new segmented-button
 * primitive, tokens only, 40 px touch minimum, non-color cue per state
 * (icon + aria-pressed). Unlike A10 this does NOT swap render engines —
 * it opens/closes the panorama overlay over the live 3D scene, so there
 * is no "switching…" pending state.
 */

import type { CSSProperties } from 'react';
import { Chip, tokens, type ChipVariant, type IconName } from '@/design';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TourRepresentation = 'bubble-tour' | 'point-cloud';

export interface TourRepresentationSwitchProps {
  /** Active representation. `bubble-tour` while the panorama overlay is open. */
  current: TourRepresentation;
  /** Called when the operator picks the other representation. */
  onChange: (representation: TourRepresentation) => void;
  /** Test id consumed by Vitest specs. Defaults to `tour-representation-switch`. */
  'data-testid'?: string;
}

// ---------------------------------------------------------------------------
// Representation metadata — token-only, non-color cue per state
// ---------------------------------------------------------------------------

interface RepresentationMeta {
  readonly label: string;
  readonly icon: IconName;
  readonly description: string;
}

const REPRESENTATION_TABLE: Record<TourRepresentation, RepresentationMeta> = {
  'bubble-tour': {
    label: 'Bubble tour',
    icon: 'vrpano',
    description: 'Walk the panorama stations captured by this scan',
  },
  'point-cloud': {
    label: 'Point cloud',
    icon: 'blur_on',
    description: 'Inspect the point cloud converted from the same scan',
  },
};

const REPRESENTATIONS: readonly TourRepresentation[] = ['bubble-tour', 'point-cloud'];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TourRepresentationSwitch({
  current,
  onChange,
  'data-testid': testId,
}: TourRepresentationSwitchProps) {
  const groupStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space[1],
    padding: tokens.space[1],
    background: tokens.color.surface[1],
    borderRadius: tokens.radius.md,
    border: tokens.border.thin,
    minHeight: tokens.density.touchMin,
  };

  return (
    <section
      aria-label="Scan representation"
      data-testid={testId ?? 'tour-representation-switch'}
      style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2] }}
    >
      <div role="group" aria-label="Representation segmented control" style={groupStyle}>
        {REPRESENTATIONS.map((representation) => {
          const meta = REPRESENTATION_TABLE[representation];
          const pressed = representation === current;
          const variant: ChipVariant = pressed ? 'info' : 'neutral';
          return (
            <Chip
              key={representation}
              label={meta.label}
              icon={meta.icon}
              variant={variant}
              pressable
              pressed={pressed}
              onClick={() => {
                if (representation === current) return;
                onChange(representation);
              }}
              aria-label={`${meta.label} — ${meta.description}`}
              data-testid={`tour-representation-switch-${representation}`}
            />
          );
        })}
      </div>
    </section>
  );
}
